import { useList, useOne, useShow } from '@refinedev/core';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useState } from 'react';

import { usePermissionContext } from '@/contexts/PermissionContext';
import { useUserContext } from '@/contexts/UserContext';
import CompanyContact from '@/model/General/CompanyContact';
import { USER_WORK_AREA } from '@/model/General/UserWorkArea';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import { PRODUCT_TYPES } from '@/model/Logistic/PackageItem/ProductTypes';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { Reception } from '@/model/Logistic/Reception/Reception';
import { RECEPTION_TYPE } from '@/model/Logistic/Reception/ReceptionType';
import { Ticket } from '@/model/Ticket/Ticket';
import { queryKeys } from '@/repositories/queryKeys';
import { RESOURCES } from '@/Resources';

const useReceptionShow = () => {
  const { userWorkArea } = useUserContext();
  const { hasPermission } = usePermissionContext();
  const [packageItems, setPackageItems] = useState<PackageItem[]>([]);
  const [blockChangeState, setBlockChangeState] = useState<{ id: string; message: string }[]>([]);
  const {
    result: record,
    query: { isLoading, refetch },
  } = useShow<Reception>();
  const {
    result: ticketResult,
    query: { isLoading: ticketIsLoading },
  } = useOne<Ticket>({
    resource: RESOURCES.TICKETS,
    id: record?.ticket || '',
    queryOptions: {
      enabled: !!record && !!record.ticket,
    },
  });
  const {
    result: createdByResult,
    query: { isLoading: createdByIsLoading },
  } = useOne({
    resource: 'users',
    id: record?.created_by || '',
    queryOptions: {
      enabled: !!record && !!record.created_by,
      retry: false,
    },
  });
  const {
    result: companyContactResult,
    query: { isLoading: companyContactIsLoading },
  } = useOne<CompanyContact>({
    resource: 'company_contact',
    id: record?.company_contact || '',
    queryOptions: {
      enabled: !!record && !!record.company_contact,
      retry: false,
    },
  });
  const loading = useMemo(
    () => isLoading || ticketIsLoading || createdByIsLoading || companyContactIsLoading,
    [isLoading, ticketIsLoading, createdByIsLoading, companyContactIsLoading],
  );
  const itemsEditMode: 'disabled' | 'enabled' | 'partial' | 'partial-with-news' = useMemo(
    () =>
      hasPermission('edit')
        ? record?.status == PACKAGE_STATES.FINALIZADO || record?.status == PACKAGE_STATES.CANCELADO
          ? 'disabled'
          : record?.status == PACKAGE_STATES.PENDIENTE
            ? record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE
              ? 'partial'
              : 'enabled'
            : userWorkArea == USER_WORK_AREA.LOGISTICS
              ? record?.tipo == RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE
                ? 'partial'
                : 'partial-with-news'
              : 'disabled'
        : 'disabled',
    [hasPermission, record, userWorkArea],
  );
  const canEditResolution = useMemo(() => {
    return userWorkArea === USER_WORK_AREA.LOGISTICS && record?.status === PACKAGE_STATES.RECIBIDO;
  }, [userWorkArea, record?.status]);
  const canFinishReception = useMemo(() => {
    return (
      userWorkArea === USER_WORK_AREA.LOGISTICS &&
      record?.status === PACKAGE_STATES.RECIBIDO &&
      packageItems.every(
        pi =>
          pi.item_received_state != undefined &&
          (pi.product_type == PRODUCT_TYPES.UNIT
            ? pi.execute_amount == 0 || pi.unidad != undefined
            : pi.product_type == PRODUCT_TYPES.DEVICE
              ? pi.execute_amount == 0 || pi.device != undefined
              : true),
      )
    );
  }, [userWorkArea, record?.status, packageItems]);
  const canMarkAsSendingReception = useMemo(
    () =>
      record?.tipo != RECEPTION_TYPE.RECEPCION_CREADA_AUTOMATICAMENTE ||
      (record?.status === PACKAGE_STATES.PENDIENTE &&
        packageItems.every(
          pi =>
            pi.requested_amount ==
            (pi.discarded_amount ?? 0) +
              (pi.technician_stock_amount ?? 0) +
              (pi.to_return_amount ?? 0),
        )),
    [record?.tipo, record?.status, packageItems],
  );
  /**
   * Refetch de paquete e items
   **/
  const queryClient = useQueryClient();
  const refetchResource = useCallback(async () => {
    await refetch();
    if (!record?.id) {
      return;
    }

    await queryClient.refetchQueries({
      queryKey: queryKeys.packageItems.byPackage(record?.id),
    });
  }, [queryClient, refetch, record?.id]);
  /**
   * Referencia para poder visualizar las OS relacionadas a recepciones que fueron previamente creadas en un envío sin técnico
   **/
  const {
    result: { data: serviceOrders },
  } = useList({
    resource: RESOURCES.SERVICE_ORDERS,
    filters: [
      {
        field: 'tickets',
        operator: 'eq',
        value: record?.ticket,
      },
    ],
    queryOptions: {
      enabled: !!record?.ticket,
    },
  });

  return {
    record,
    ticket: ticketResult,
    createdBy: createdByResult,
    companyContact: companyContactResult,
    loading,
    canEditResolution,
    canFinishReception,
    canMarkAsSendingReception,
    blockChangeState,
    setBlockChangeState,
    packageItems,
    setPackageItems,
    itemsEditMode,
    refetchResource,
    serviceOrders,
  };
};

export default useReceptionShow;
